/**
 * Hooks da feature Shift Handover (GUPM-104).
 * Tudo passa pelo datasource — nunca chama supabase direto.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataSources } from '@/services/datasources';
import { useAuth } from '@/hooks/useAuth';
import type { ChecklistResponse } from '@/domain/shift-handover/types';
import type { TotpErrorCode } from '@/domain';
import type {
  CreateHandoverInput,
  ListHandoversFilter,
  OpenShiftInput,
  ShiftHandover,
  TotpVerifyResult,
  TotpEnrollment,
} from '@/services/datasources';

export interface CurrentEngineer {
  id: string;
  name: string;
  email: string;
  profile: 'operador' | 'tecnico_responsavel' | 'admin';
  barragemIds: string[];
}

export function useCurrentEngineer() {
  // Delega ao datasource — hooks não chamam supabase.from() direto (CLAUDE.md).
  // Inclui user.id no queryKey pra invalidar cache ao trocar de usuário.
  const { user } = useAuth();
  const { shiftHandover } = useDataSources();
  return useQuery<CurrentEngineer | null>({
    queryKey: ['current-engineer', user?.id ?? 'anonymous'],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: () => shiftHandover.getCurrentUser(),
  });
}

export function useChecklistCatalog() {
  const { shiftHandover } = useDataSources();
  return useQuery({
    queryKey: ['shift-checklist-items'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => shiftHandover.listChecklistItems(),
  });
}

export function useActiveOperators(damCode: string | undefined) {
  const { shiftHandover } = useDataSources();
  return useQuery({
    queryKey: ['shift-active-operators', damCode],
    enabled: !!damCode,
    staleTime: 60 * 1000,
    queryFn: () => shiftHandover.listActiveOperators(damCode!),
  });
}

/**
 * Plantão ativo — visível a qualquer perfil autenticado (RPC security definer;
 * a RLS da tabela esconderia a troca do operador que não participou).
 *
 * Sem barragem: o plantão é GLOBAL, quem está de plantão cobre A, B e C.
 * GUPM-261.
 */
export function useCurrentShift() {
  const { shiftHandover } = useDataSources();
  const { user } = useAuth();
  return useQuery({
    queryKey: ['shift-current-shift'],
    enabled: !!user,
    queryFn: () => shiftHandover.getCurrentShift(),
  });
}

/**
 * Abertura de plantão (GUPM-272) — a operação está sem plantonista e quem
 * registra assume. Um TOTP só: o de quem assume. Não existe segundo código
 * porque não existe quem passe.
 *
 * O entrante nunca vai no input: quem assume é sempre o usuário da sessão. Era
 * essa escolha, oferecida em dropdown, que punha o plantão no nome de terceiro.
 */
export function useOpenShift() {
  const { shiftHandover } = useDataSources();
  const { data: engineer } = useCurrentEngineer();
  const qc = useQueryClient();
  return useMutation<ShiftHandover, Error, OpenShiftInput & { code: string }>({
    mutationFn: async ({ code, ...input }) => {
      if (!engineer) throw new Error('usuário não resolvido — recarregue a página');
      const totp = await shiftHandover.verifyTotp(engineer.id, code, 'plantao_abertura');
      if (!totp.success) throw new Error(TOTP_ERROR_MESSAGES[totp.error]);
      return shiftHandover.openShift(input);
    },
    // `onSettled` pela mesma razão do `useCreateShiftHandover`: a recusa por
    // cadeia ("já existe plantão ativo") significa que esta tela está velha, e
    // é exatamente o caso em que ela continua oferecendo "Abrir plantão".
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['shift-current-shift'] });
      qc.invalidateQueries({ queryKey: ['shift-handover-history'] });
    },
  });
}

/**
 * Encerramento do plantão sem iniciar outro. Autorizado ao próprio plantonista
 * (auto-encerramento) e a admin ou técnico responsável (destrave, GUPM-313) —
 * gate no banco. Motivo obrigatório e **TOTP de quem encerra validado antes da
 * escrita**.
 *
 * `responses` só vem no auto-encerramento, feito pelo formulário de troca com
 * "— Ninguém —" na lista de entrantes: quem larga a vigilância atesta como a
 * barragem ficou (GUPM-272 Rev.2). O destrave não manda checklist — quem
 * destrava não viveu o turno.
 *
 * O TOTP é verificado pela Edge Function e a escrita ocorre em seguida — mesmo
 * padrão da troca de plantão e da baixa da TARP. Se o código não valida, nada
 * é escrito e o erro tipado sobe para o modal. GUPM-261.
 */
export function useCloseShift() {
  const { shiftHandover } = useDataSources();
  const { data: engineer } = useCurrentEngineer();
  const qc = useQueryClient();
  return useMutation<void, Error, { reason: string; code: string; responses?: ChecklistResponse[] }>({
    mutationFn: async ({ reason, code, responses }) => {
      if (!engineer) throw new Error('usuário não resolvido — recarregue a página');
      const totp = await shiftHandover.verifyTotp(engineer.id, code, 'plantao_encerramento');
      if (!totp.success) throw new Error(TOTP_ERROR_MESSAGES[totp.error]);
      await shiftHandover.closeCurrentShift(reason, responses);
    },
    // Idem: encerramento recusado (deixou de ser holder, alguém destravou antes)
    // deixa a tela oferecendo um encerramento que não existe mais.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['shift-current-shift'] });
      qc.invalidateQueries({ queryKey: ['shift-handover-history'] });
    },
  });
}

export function useHandoverHistory(filter: ListHandoversFilter) {
  const { shiftHandover } = useDataSources();
  return useQuery({
    queryKey: ['shift-handover-history', filter],
    queryFn: () => shiftHandover.listHandovers(filter),
    staleTime: 30 * 1000,
  });
}

export interface CreateHandoverWithTotpInput extends CreateHandoverInput {
  /** Engineer.id do saindo (sessão atual). */
  saindoEngineerId: string;
  /** Código TOTP do saindo. */
  saindoCode: string;
  /** Código TOTP do entrante. */
  entranteCode: string;
}

const TOTP_ERROR_MESSAGES: Record<TotpErrorCode, string> = {
  invalid_code: 'Código TOTP incorreto. Confira no Microsoft Authenticator e tente novamente.',
  locked_out:
    'Acesso bloqueado por excesso de tentativas. Aguarde 15 minutos ou contate o admin.',
  no_totp_set:
    'Autenticador ainda não cadastrado para este operador. Acesse /perfil/totp antes de operar troca.',
  unauthorized: 'Sessão expirada. Faça login novamente.',
  service_unavailable:
    'Serviço de verificação indisponível no momento. Tente novamente em instantes.',
  not_found: 'Operador não encontrado.',
  enrollment_self_only:
    'Erro interno: tentativa de validar enrollment de outro usuário (não deveria acontecer em troca de plantão).',
};

function totpErrorMessage(result: Extract<TotpVerifyResult, { success: false }>, who: 'saindo' | 'entrante') {
  const base = TOTP_ERROR_MESSAGES[result.error];
  return `[${who}] ${base}`;
}

export function useCreateShiftHandover() {
  const { shiftHandover } = useDataSources();
  const qc = useQueryClient();

  return useMutation<ShiftHandover, Error, CreateHandoverWithTotpInput>({
    mutationFn: async (input) => {
      const saindoResult = await shiftHandover.verifyTotp(
        input.saindoEngineerId,
        input.saindoCode,
        'plantao_troca_saida',
      );
      if (!saindoResult.success) throw new Error(totpErrorMessage(saindoResult, 'saindo'));

      const entranteResult = await shiftHandover.verifyTotp(
        input.operatorInId,
        input.entranteCode,
        'plantao_troca_entrada',
      );
      if (!entranteResult.success) throw new Error(totpErrorMessage(entranteResult, 'entrante'));

      const handoverInput = {
        operatorInId: input.operatorInId,
        responses: input.responses,
        observation: input.observation,
      };
      return shiftHandover.createHandover(handoverInput);
    },
    // `onSettled` e não `onSuccess`: a revalidação importa MAIS no erro que no
    // acerto. Quando o banco recusa por cadeia ("já existe plantão ativo"), a
    // recusa é a prova de que a tela está com leitura velha — e sem reler ela
    // segue oferecendo a mesma ação, que vai falhar de novo. GUPM-273.
    onSettled: () => {
      // `shift-current-shift` não tem barragem na chave: o plantão é global.
      qc.invalidateQueries({ queryKey: ['shift-current-shift'] });
      // TanStack faz prefix-match em invalidateQueries — invalida TODOS os filtros
      // de histórico (qualquer barragem/período) pra evitar cache stale na tela
      // /historico-trocas após registrar uma troca nova.
      qc.invalidateQueries({ queryKey: ['shift-handover-history'] });
    },
  });
}

export function useProvisionTotp() {
  const { shiftHandover } = useDataSources();
  return useMutation<TotpEnrollment, Error, void>({
    mutationFn: () => shiftHandover.provisionTotp(),
  });
}

export function useConfirmTotpEnrollment() {
  const { shiftHandover } = useDataSources();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (secret) => shiftHandover.confirmTotpEnrollment(secret),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['current-engineer'] });
    },
  });
}

export type { ChecklistResponse };
